import React from 'react';
import {
  Button, Card, Badge, TextInput, Textarea, Select, SegmentedControl, SearchInput,
  BottomSheet, EmptyState, IconButton, Alert,
} from '@parsina/framework';
import { ClipboardList, Sparkles, Ban, FlaskConical } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { bffQuery, errorCode, errorMessageKey } from '../../lib/bffClient.js';
import { ORDER_CATALOG_QUERY } from '../../graphql/documents.js';
import {
  ORDER_TYPES, ORDER_URGENCIES, orderTypeKey, urgencyKey, orderStatusKey,
  orderStatusVariant, urgencyVariant, originKey,
} from '../../constants/vocabulary.js';
import { groupOrders, flattenCatalog } from '../../screens/Visit/Visit.shared.js';
import { CostChip } from '../common/ParsehCost.jsx';

// دستورات — what the student asks for, and what they then pretend came back.
//
// ── The result is entered by the student, and that is the exercise ───────
//
// There is no lab, no PACS and no interface: `recordOrderResult` writes the
// SIMULATED result the student types (core.md §5.2, `result_body` +
// `result_structured_json`). The point of the exercise is reading a result
// against the clinical picture they wrote an hour earlier — so the sheet takes
// free text first and structured rows second, never the other way round.
//
// ── «لغو» is a status, not a delete ──────────────────────────────────────
//
// `cancelOrder` sets `cancelled` and the row stays on screen. An order that was
// placed and then withdrawn is part of the reasoning; deleting it would leave a
// timeline in which it was never ordered at all.

const emptyForm = { orderType: 'lab', title: '', code: '', category: '', urgency: 'routine', notes: '' };

/** The three lists the catalog actually carries (`OrderCatalog` in the schema). */
const CATALOG_TYPES = ['lab', 'imaging', 'procedure'];

/** The result flags a structured lab row may carry. Letters, as a lab report prints them. */
const RESULT_FLAGS = ['L', 'H', 'N'];

/**
 * The catalog picker: one tab per ORDER TYPE, a search box and a list.
 *
 * core.md §7.5 lists five types and the catalog holds three lists. The two the
 * catalog has nothing for — «ارجاع» and «سایر» — are still tabs here, and
 * choosing one opens the free-text form pre-set to that type: every type is one
 * click away from the same control, rather than three from here and two from a
 * dropdown on a different form.
 */
function CatalogPicker({ open, onClose, onPick, onFreeText }) {
  const { t } = useTranslation();
  const [type, setType] = React.useState('lab');
  const [query, setQuery] = React.useState('');
  const [catalog, setCatalog] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open || catalog) return;
    setLoading(true);
    bffQuery(ORDER_CATALOG_QUERY)
      .then((data) => setCatalog(data?.orderCatalog || null))
      .catch((err) => {
        // The catalog is a convenience over free text, never a gate: an order
        // list that cannot be written because a lookup failed is worse than an
        // order list with a typed title.
        console.warn('[OrdersPanel] catalog failed:', errorCode(err) || err?.message);
      })
      .finally(() => setLoading(false));
  }, [open, catalog]);

  if (!open) return null;

  const all = flattenCatalog(catalog).filter((row) => row.orderType === type);
  const text = query.trim().toLowerCase();
  const rows = text
    ? all.filter((r) => `${r.name} ${r.nameEn || ''} ${r.code || ''}`.toLowerCase().includes(text))
    : all;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('visit.orders.pickTitle')}
      fullHeight
      maxWidth={640}
      closeLabel={t('common.actions.close')}
      data-testid="catalog-picker"
    >
      <SegmentedControl
        options={ORDER_TYPES.map((id) => ({ value: id, label: t(orderTypeKey(id)) }))}
        value={type}
        onChange={(next) => {
          if (CATALOG_TYPES.includes(next)) { setType(next); return; }
          onFreeText?.(next);
        }}
        ariaLabel={t('visit.orders.type')}
        data-testid="catalog-types"
      />
      <SearchInput
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('visit.orders.search')}
        style={{ margin: '10px 0' }}
        data-testid="catalog-search"
      />
      {loading && <p style={{ fontSize: 12.5, color: CT.mute }}>{t('common.labels.loading')}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map((row) => (
          <button
            key={`${row.orderType}-${row.code || row.name}`}
            type="button"
            onClick={() => onPick(row)}
            style={{
              border: 0, background: 'none', font: 'inherit', textAlign: 'start',
              padding: '8px 6px', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, color: C.textPrimary,
            }}
            data-testid={`catalog-option-${row.code || row.name}`}
          >
            {row.name}
            {row.category && (
              <span style={{ fontSize: 11.5, color: CT.mute, marginInlineStart: 6 }}>{row.category}</span>
            )}
          </button>
        ))}
        {!rows.length && !loading && (
          <p style={{ fontSize: 12.5, color: CT.mute }}>{t('visit.orders.pickEmpty')}</p>
        )}
      </div>
    </BottomSheet>
  );
}

/**
 * «ثبت نتیجه» — free text, plus optional structured rows for a lab.
 *
 * An EMPTY result is refused here, before the wire: the BFF answers
 * INVALID_INPUT for one, and a sheet that lets the button be pressed only to
 * bounce is a sheet that taught the student the button is broken. And when the
 * BFF does refuse (a row shape it will not take), the sheet STAYS OPEN with the
 * message — closing over a banner the sheet has just covered is the same lesson.
 */
export function ResultSheet({ open, onClose, order, onSave }) {
  const { t } = useTranslation();
  const [body, setBody] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setBody(order?.resultBody || '');
    setRows(Array.isArray(order?.resultStructured) ? order.resultStructured : []);
    setError(null);
  }, [open, order]);

  if (!open) return null;

  const setRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  // Only rows with a name; an empty template row is not a result.
  const namedRows = rows.filter((r) => (r.name || '').trim());
  const empty = !body.trim() && !namedRows.length;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('visit.orders.resultTitle')}
      fullHeight
      maxWidth={680}
      closeLabel={t('common.actions.close')}
      data-testid="result-sheet"
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            disabled={saving || empty}
            onClick={async () => {
              setSaving(true);
              setError(null);
              try {
                await onSave({
                  resultBody: body.trim() || null,
                  resultStructured: namedRows.length ? namedRows : null,
                });
                onClose();
              } catch (err) {
                setError(err);
              } finally { setSaving(false); }
            }}
            data-testid="result-save"
          >
            {t('visit.orders.resultSave')}
          </Button>
          <Button variant="outline" onClick={onClose}>{t('common.actions.cancel')}</Button>
        </div>
      )}
    >
      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700 }}>{order?.title}</p>
      {error && (
        <Alert variant="error" title={t(errorMessageKey(error))} style={{ marginBottom: 10 }} data-testid="result-error">
          {error.message}
        </Alert>
      )}
      {empty && (
        <p style={{ margin: '0 0 8px', fontSize: 12, color: CT.mute }} data-testid="result-empty-hint">
          {t('visit.orders.resultEmpty')}
        </p>
      )}
      <Textarea
        label={t('visit.orders.resultBody')}
        rows={4}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        data-testid="result-body"
      />
      {order?.orderType === 'lab' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{t('visit.orders.resultRows')}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRows((prev) => [...prev, { name: '', value: '', unit: '', ref: '', flag: '' }])}
              data-testid="result-add-row"
            >
              {t('visit.orders.resultAddRow')}
            </Button>
          </div>
          {rows.map((row, i) => (
            <div
              key={`row-${i}`}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, marginBottom: 8 }}
            >
              <TextInput
                value={row.name || ''}
                placeholder={t('visit.orders.resultName')}
                onChange={(e) => setRow(i, { name: e.target.value })}
                data-testid={`result-name-${i}`}
              />
              <TextInput
                value={row.value || ''}
                placeholder={t('visit.orders.resultValue')}
                onChange={(e) => setRow(i, { value: e.target.value })}
              />
              <TextInput
                value={row.unit || ''}
                placeholder={t('visit.orders.resultUnit')}
                onChange={(e) => setRow(i, { unit: e.target.value })}
              />
              <TextInput
                value={row.ref || ''}
                placeholder={t('visit.orders.resultRef')}
                onChange={(e) => setRow(i, { ref: e.target.value })}
              />
              <Select
                value={row.flag || ''}
                onChange={(e) => setRow(i, { flag: e.target.value })}
                options={RESULT_FLAGS.map((f) => ({ value: f, label: f }))}
                placeholder={t('visit.orders.resultFlagNone')}
                aria-label={t('visit.orders.resultFlag')}
                data-testid={`result-flag-${i}`}
              />
            </div>
          ))}
        </div>
      )}
    </BottomSheet>
  );
}

export default function OrdersPanel({
  orders = [],
  readOnly = false,
  onAdd,
  onCancel,
  onRecordResult,
  onSuggest,
  suggestCost,
  suggestBusy = false,
  onOpenSuggestions,
  hasSuggestions = false,
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [resultFor, setResultFor] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const groups = groupOrders(orders);

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onAdd({
        orderType: form.orderType,
        title: form.title.trim(),
        code: form.code || null,
        category: form.category || null,
        urgency: form.urgency,
        notes: form.notes || null,
        origin: 'manual',
      });
      setForm(emptyForm);
      setAdding(false);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="orders-panel">
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => setAdding((v) => !v)} data-testid="order-add">
            {t('visit.orders.add')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="order-catalog">
            {t('visit.orders.fromCatalog')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onSuggest}
            disabled={suggestBusy}
            data-testid="order-suggest"
          >
            <Sparkles size={14} aria-hidden style={{ marginInlineEnd: 6 }} />
            {suggestBusy ? t('visit.orders.suggestBusy') : t('visit.orders.suggest')}
          </Button>
          <CostChip cost={suggestCost} before />
          {hasSuggestions && (
            <Button size="sm" variant="ghost" onClick={onOpenSuggestions} data-testid="order-suggest-open">
              {t('visit.orders.suggestLast')}
            </Button>
          )}
        </div>
      )}

      {adding && !readOnly && (
        <Card style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="order-form">
          <TextInput
            label={t('visit.orders.title')}
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            data-testid="order-title"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <Select
              label={t('visit.orders.type')}
              value={form.orderType}
              onChange={(e) => setForm((f) => ({ ...f, orderType: e.target.value }))}
              options={ORDER_TYPES.map((id) => ({ value: id, label: t(orderTypeKey(id)) }))}
              placeholder=""
              data-testid="order-type"
            />
            <Select
              label={t('visit.orders.urgency')}
              value={form.urgency}
              onChange={(e) => setForm((f) => ({ ...f, urgency: e.target.value }))}
              options={ORDER_URGENCIES.map((id) => ({ value: id, label: t(urgencyKey(id)) }))}
              placeholder=""
              data-testid="order-urgency"
            />
          </div>
          <Textarea
            label={t('visit.orders.notes')}
            rows={2}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="order-notes"
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" onClick={submit} disabled={saving || !form.title.trim()} data-testid="order-submit">
              {t('common.actions.save')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setAdding(false); setForm(emptyForm); }}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </Card>
      )}

      {!groups.length && (
        <EmptyState
          icon={<ClipboardList size={44} strokeWidth={1.5} aria-hidden />}
          title={t('visit.orders.empty')}
          description={t('visit.orders.emptyBody')}
          data-testid="orders-empty"
        />
      )}

      {groups.map((group) => (
        <section key={group.type} data-testid={`order-group-${group.type}`}>
          <h3 style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 800, color: CT.mute }}>
            {t(orderTypeKey(group.type))}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.rows.map((row) => (
              <Card key={row.id} style={{ padding: 10 }} data-testid={`order-row-${row.id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700 }}>{row.title}</span>
                  <Badge variant={urgencyVariant(row.urgency)}>{t(urgencyKey(row.urgency))}</Badge>
                  <Badge variant={orderStatusVariant(row.status)}>{t(orderStatusKey(row.status))}</Badge>
                  {row.origin === 'ai' && (
                    <Badge variant="warning" data-testid={`order-origin-${row.id}`}>
                      {t(originKey(row.origin))}
                    </Badge>
                  )}
                  {!readOnly && row.status !== 'cancelled' && (
                    <span style={{ marginInlineStart: 'auto', display: 'flex', gap: 6 }}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setResultFor(row)}
                        data-testid={`order-result-${row.id}`}
                      >
                        <FlaskConical size={13} aria-hidden style={{ marginInlineEnd: 5 }} />
                        {t('visit.orders.recordResult')}
                      </Button>
                      <IconButton
                        size={30}
                        variant="ghost"
                        icon={<Ban size={15} aria-hidden />}
                        label={t('visit.orders.cancelOrder')}
                        onClick={() => onCancel(row.id)}
                        data-testid={`order-cancel-${row.id}`}
                      />
                    </span>
                  )}
                </div>
                {row.notes && (
                  <p style={{ margin: '6px 0 0', fontSize: 12.5, color: CT.ink2 }}>{row.notes}</p>
                )}
                {row.resultBody && (
                  <p style={{
                    margin: '8px 0 0', padding: 8, borderRadius: 8, background: CT.bg,
                    fontSize: 12.5, lineHeight: 1.9, whiteSpace: 'pre-wrap', color: C.textPrimary,
                  }}
                  >
                    {row.resultBody}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </section>
      ))}

      <CatalogPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(row) => {
          setForm({
            orderType: row.orderType || 'lab',
            title: row.name,
            code: row.code || '',
            category: row.category || '',
            urgency: 'routine',
            notes: '',
          });
          setPickerOpen(false);
          setAdding(true);
        }}
        onFreeText={(orderType) => {
          // «ارجاع» / «سایر»: no list to pick from — the form, pre-set to the type.
          setForm({ ...emptyForm, orderType });
          setPickerOpen(false);
          setAdding(true);
        }}
      />

      <ResultSheet
        open={!!resultFor}
        order={resultFor}
        onClose={() => setResultFor(null)}
        onSave={(input) => onRecordResult(resultFor.id, input)}
      />
    </div>
  );
}
